import React, { useState } from 'react';
import { Form, Input, Button, Typography, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone, SunOutlined, MoonOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import './Login.css';

const { Title, Text } = Typography;

/** Telefonu ekranda "532 000 00 00" formatında gösterir */
function formatPhoneDisplay(phone: string | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
}

/** Maskeli input; form değeri sadece rakam (5320000000) olarak tutulur */
const PhoneInput: React.FC<{ value?: string; onChange?: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => (
  <Input
    prefix={<UserOutlined />}
    placeholder={placeholder ?? '532 000 00 00'}
    value={formatPhoneDisplay(value)}
    onChange={(e) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      onChange?.(digits);
    }}
  />
);

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const onFinish = async (values: { phoneNumber: string; password: string }) => {
    setLoading(true);
    const phoneDigits = (values.phoneNumber || '').replace(/\D/g, '').slice(0, 10);
    const success = await login(phoneDigits, values.password);
    setLoading(false);
    if (success) {
      navigate('/dashboard');
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-theme-toggle">
        <Button
          type="text"
          icon={theme === 'light' ? <MoonOutlined /> : <SunOutlined />}
          onClick={toggleTheme}
          size="large"
          title={theme === 'light' ? 'Karanlık mod' : 'Aydınlık mod'}
        />
      </div>
      <div className="login-left">
        <div className="login-illustration">
 
          <div> <img src='../images/logolight.png' style={{ width: '300px' }}></img> </div>
        </div>
      </div>
      <div className="login-right">
        <div className="login-content">
          <div className="login-header">

          <div> <img src={theme === 'dark' ? '../images/logo-print-dark.png' : '../images/logo-print.png'} alt="Logo" style={{ width: '300px' }} /> </div>

            <Title level={2}>Kullanıcı Girişi</Title>
            <Text type="secondary"> Bu sistem yalnızca yetkilendirilmiş kullanıcılar tarafından kullanılabilir. Tüm işlemler ve erişim denemeleri güvenlik amacıyla kayıt altına alınmaktadır. Yetkisiz erişim girişimleri yasal işleme tabi tutulabilir.</Text>
          </div>
          
          <Form
            name="login"
            onFinish={onFinish}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item
              name="phoneNumber"
              label="Telefon Numarası"
              rules={[
                { required: true, message: 'Telefon numarası gerekli!' },
                {
                  validator: (_, value) => {
                    const digits = (value ?? '').toString().replace(/\D/g, '');
                    if (digits.length !== 10) {
                      return Promise.reject(new Error('Geçerli bir telefon numarası giriniz! (532 000 00 00)'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <PhoneInput />
            </Form.Item>

            <Form.Item
              name="password"
              label="Şifre"
              rules={[{ required: true, message: 'Şifre gerekli!' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Şifrenizi giriniz"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <div className="login-options">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>Beni Hatırla</Checkbox>
              </Form.Item>
             
            </div>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                Giriş Yap
              </Button>
            </Form.Item>
          </Form>

          
          
        </div>
      </div>
    </div>
  );
};

export default Login;
